/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [C1f §7] ONE CONTROL, FOUR MEANINGS — AND A STATED REVERSAL OF [P3a]'s TOOLBAR.
 *
 *  ⚠️ P3a SHIPPED THREE BUTTONS AND [D4.5] ADDED A FOURTH. `Save draft` ·
 *  `Assign names myself` · `Publish and request divisions`, plus `Discard`. C1f reduces the
 *  action group to TWO — `Discard`, and one control whose meaning changes — and that is a
 *  reversal rather than a tidy-up, so it is written down here where somebody restoring the
 *  third would have to read it.
 *
 *  What went, and why each could go:
 *
 *    • `Save draft` — §6 restored autosave, so it has no work left to do. ⚠️ THE PREMISE THE
 *      BRIEF GAVE FOR THIS WAS FALSE: there was no autosave to lean on, [P3b.3] having
 *      removed it, so C1f had to BUILD one before the button could go. `autosave.ts` carries
 *      that argument.
 *    • `Assign names myself` — the BUTTON goes. The publish INTENT stays on the wire
 *      untouched: `setup.spec.ts` posts `ASSIGN_MYSELF` at six places to build the fixtures
 *      every other e2e project reads, `standingOrderGenerator.service.ts` passes the other
 *      through the same body, and the value is recorded in the `ARRANGEMENT_PUBLISHED` audit
 *      context. Removing a button is not removing an API.
 *    • The qualifier on `Publish and request divisions` — it existed to distinguish that
 *      button from the one beside it, and the one beside it is gone.
 *
 *  ⚠️ AND "AFTER PUBLISH" IS A REAL STATE, not a transient after-effect of the publish click.
 *  `ArrangementDetail`'s `Edit` opens `/arrangements/:id/edit` on a PUBLISHED order, and a
 *  pasted URL does the same — so the builder is opened on an issued order routinely, and that
 *  is when the control reads `Assign names`.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
import { saveIsPending, type SaveState } from './autosave';

export const PUBLISH_CONTROL_STATES = ['published', 'refused', 'save', 'blocked', 'ready'] as const;
export type PublishControlState = (typeof PUBLISH_CONTROL_STATES)[number];

export type PublishControlInput = {
  canPublish: boolean;
  savedId: string | undefined;
  /**
   * ⚠️ POSITIVE — "the record has ARRIVED and it is not a draft", never `!isDraft`.
   *
   * `isDraft` is deliberately hide-then-show for the destructive `Discard`: an unresolved
   * record answers "not a draft" so the button cannot flicker in a moment later. Read as
   * `!isDraft` it would put `Assign names` on a DRAFT that is still loading.
   */
  publishedRecord: boolean;
  blockerCount: number;
  /**
   * ⚠️ [C3a §7d] THE SAME VALUE THE SAVE PATH WRITES, AND NEVER A SECOND `dirty` FLAG.
   *
   * [C2a]'s data loss was three facts about one save — the `dirty` state, the `dirtyRef` and the
   * publish handler — kept in step by hand, and one of them forgotten. `index.tsx`'s `markDirty`
   * / `markClean` are now the only writers of any of them, and this reads what they wrote.
   */
  saveState: SaveState;
  /**
   * ⚠️ [AUTO-SAVE] `ENABLE_AUTO_SAVE`, AS THE PAGE READ IT ON MOUNT — REQUIRED, NEVER DEFAULTED.
   *
   * A default would put one mode in the omitted position, and the omission would ship silently:
   * with autosave off and this read as on, an edit to an issued order renders `Assign names` and no
   * Save at all, so the edit has no way to reach the server — [P5c.0]'s rule for this family.
   */
  autoSave: boolean;
};

/**
 * ⚠️ THE ORDER IS THE ARGUMENT.
 *
 * `published` is FIRST and is capability-free: `/arrangements/:id` is gated on
 * `arrangement.read` and `Assign names` writes nothing, so a read-only viewer on an issued
 * order must still be offered the door to the naming screen.
 *
 * `refused` comes before `unsaved` because a capability answer does not depend on whether
 * anything has been saved — telling a viewer to save first would be advice they cannot take.
 */
export const publishControlState = ({
  publishedRecord,
  canPublish,
  savedId,
  blockerCount,
  saveState,
  autoSave,
}: PublishControlInput): PublishControlState => {
  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [AUTO-SAVE] WITH AUTOSAVE OFF, A SAVE THAT IS OWED COMES FIRST — AN ISSUED ORDER INCLUDED.
   *
   *  With autosave on, `published` may outrank the save: an edit to an issued order — whatever
   *  [C3c §9] leaves open, force counts and command entries among it — is written on the next blur,
   *  and a failed write is retried on the one after. With it off, this button is the ONLY way such
   *  an edit reaches the server, so `Assign names` over an unsaved edit would hold it for ever.
   *
   *  ⚠️ IT CANNOT BE "ADVICE A VIEWER CANNOT TAKE" — the reason `refused` outranks the save below.
   *  `readOnly` is `!canWrite`, a capability fixed for the mount, and a read-only form never marks
   *  itself dirty, so a save is owed only to somebody who may make it. Autosave ON is unchanged.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  if (!autoSave && saveIsPending(saveState)) return 'save';
  if (publishedRecord) return 'published';
  if (!canPublish) return 'refused';
  /*
   * ─────────────────────────────────────────────────────────────────────────────────────
   *  ⚠️ [C3a §7a] A SAVE IS OWED — SO THE CONTROL IS `Save` AND PUBLISH IS NOT RENDERED.
   *
   *  This WIDENS the old `unsaved` arm rather than joining it: that one meant "there is no id
   *  yet", which is one of the ways a save can be owed and not the only one. The gap §7 names is
   *  the other: once a draft HAS an id, a FAILED save left Publish primary and fully offered.
   *
   *  ⚠️ AN UNTOUCHED CREATE FORM IS A `save` TOO, AND THE RENDER DISABLES IT — WHICH CLOSES A
   *  LIVE HOLE RATHER THAN OPENING ONE. Today that form shows an unavailable-LOOKING Publish
   *  that is genuinely clickable and, when clicked, calls `flush` → `armingOf` → `'no-content'`
   *  → returns SILENTLY. A control that does nothing is exactly what [C1f §7b] forbids, and it
   *  has shipped since that pass. `saveState === 'idle'` is render-safe and is the honest gate:
   *  "nothing to save yet" said out loud, rather than a button that lies about being pressable.
   *
   *  ⚠️ 7b IS SATISFIED BY THAT, NOT BY HIDING THE CONTROL. Its rule is that a Save visible in
   *  the ORDINARY case means autosave is broken — and a DISABLED one on a form nobody has typed
   *  into is not a claim that a save is owed.
   * ─────────────────────────────────────────────────────────────────────────────────────
   */
  if (saveIsPending(saveState) || savedId === undefined) return 'save';
  return blockerCount > 0 ? 'blocked' : 'ready';
};

/**
 * ⚠️ [C1f §7b] UNAVAILABLE-LOOKING, AND GENUINELY CLICKABLE — NEITHER `disabled` NOR
 * `aria-disabled`.
 *
 * [P4-UX] kept the blocked Publish genuinely `disabled` and wrapped it in a click-through
 * `<span>`, because a disabled button has `pointer-events: none`. Its header refused
 * `aria-disabled` on a claim that is TRUE — MEASURED against jest-dom 7.0.0, whose
 * `isElementDisabled` reads `el.hasAttribute('disabled')` and nothing else, so
 * `not.toBeDisabled()` would have passed vacuously.
 *
 * ⚠️ IT FAILS A SECOND WAY THAT ONLY MATTERS NOW THAT §7b ASKS FOR CLICKABILITY. MEASURED
 * against playwright-core 1.62.1: `getAriaDisabled` is
 * `isNativelyDisabled || hasExplicitAriaDisabled`, and `click` waits on `enabled` — so an
 * `aria-disabled` button could be clicked only with `{ force: true }`, and the browser
 * assertion would be testing a gesture no operator makes. It is also a lie to a screen-reader
 * user: a control announced "unavailable" that opens a dialog when pressed.
 *
 * So the blocked control is a REAL, ENABLED, FOCUSABLE button that LOOKS unavailable and SAYS
 * why — which makes §7b's keyboard path real for the first time, and DELETES the
 * click-through span rather than adding to it.
 *
 * ⚠️ `refused` ALONE STAYS GENUINELY `disabled`, and its cost is stated rather than hidden: a
 * disabled button is not focusable, so its tooltip is pointer-only. That is accepted here and
 * refused for `blocked` because the two are different facts — a blocker is a to-do list the
 * operator can act on, and a capability refusal is permanent for that operator with nothing
 * behind it. `READ_ONLY_TOOLTIP` on a disabled button is this app's idiom in ten other places.
 *
 * ⚠️ AND IT IS A UTILITY STRING, NOT `!important`. Tailwind sits in `@layer utilities`, above
 * `@layer antd`, so these beat AntD's primary styling with nothing this repository forbids.
 */
const UNAVAILABLE = 'cursor-not-allowed border-border bg-muted text-muted-foreground shadow-none hover:bg-muted';

/**
 * ⚠️ [amended C3a §7a] `save` CARRIES NO UNAVAILABLE LOOK, AND THAT REVERSES NOTHING.
 *
 * [C1f §7b]'s rule is that a control which cannot publish LOOKS unavailable and is genuinely
 * clickable. The control that cannot publish is no longer the publish control — it is a Save
 * button, whose whole job is to be pressed. Its `blocked` half is untouched, `disabled` and
 * `aria-disabled` are still both refused for the measured reasons above, and `refused` alone is
 * still genuinely `disabled`.
 */
export const UNAVAILABLE_LOOK: Record<PublishControlState, string | undefined> = {
  published: undefined,
  refused: undefined,
  save: undefined,
  blocked: UNAVAILABLE,
  ready: undefined,
};
